import SEO from "@/components/SEO";
import Wishlist from "@/components/Wishlist";
import { store, addAppListener } from "@/lib/redux/store";
import {
  wishlistSchema,
  createWishlist,
  wishlistLineAdd,
  wishlistLineRemove,
  wishlistLineUpdate,
} from "@/lib/redux/wishlist/wishlistSlice";
import {
  MainLayoutQuery,
  getMainLayoutQuery,
  settingsQuery,
  seoQuerySchema,
} from "@/lib/sanity/queries";
import { isAnyOf } from "@reduxjs/toolkit";
import { sanityClient } from "@/lib/sanity/server";
import { GetStaticProps, NextPage } from "next";
import { mergeWith } from "lodash";
import { useEffect, useMemo } from "react";
import { groq } from "next-sanity";

type Props = {
  layout: MainLayoutQuery;
  page: any;
  settings: any;
  queryParams: {
    locale: string;
    defaultLocale: string;
  };
};

const pageQuery = groq`
*[_type == "wishlistPage"][0]{
   _id,
  "slug": slug.current,
  seo {
    "metaTitle": coalesce(metaTitle[$locale], metaTitle[$defaultLocale]),
    "metaDesc": coalesce(metaDesc[$locale], metaDesc[$defaultLocale]),
    "shareTitle": coalesce(shareTitle[$locale], shareTitle[$defaultLocale]),
    "shareDesc": coalesce(shareDesc[$locale], shareDesc[$defaultLocale]),
    shareGraphic {
      asset->{
        ...,
        "alt": coalesce(alt[$locale], alt[$defaultLocale])
      }
    }
  },
  form {
    _key,
    sendToCRM,
    "heading": coalesce(heading[$locale], heading[$defaultLocale]),
    "shortText": coalesce(shortText[$locale], shortText[$defaultLocale]),
    "buttonText": coalesce(buttonText[$locale], buttonText[$defaultLocale]),
    sendToCRM,
    templateId,
    sendTo,
    "validErrorText": coalesce(validErrorText[$locale], validErrorText[$defaultLocale]),
    fields[] {
      ...,
      "label": coalesce(label[$locale], label[$defaultLocale]),
      "placeholder": coalesce(placeholder[$locale], placeholder[$defaultLocale]),
      "inputValidErrorText": coalesce(inputValidErrorText[$locale], inputValidErrorText[$defaultLocale]),
      items[]{
        ...,
        "value": coalesce(value[$locale], value[$defaultLocale]),
        "label": coalesce(value[$locale], value[$defaultLocale]),
      }
    }
  }
}
`;

// const pageQuerySchema = z.array(
//   z.object({
//     _id: z.string().optional(),
//     slug: z.string().optional(),
//     form: z.object({
//       heading: z.string().optional(),
//       shortText: z.string().optional(),
//       buttonText: z.string().optional(),
//       templateId: z.string().optional(),
//       sendTo: z.string().optional(),
//       validErrorText: z.any().optional(),
//       fields: z.array(z.record(z.string(), z.any())).optional(),
//     }),
//   })
// );

// type PageQuery = z.infer<typeof pageQuerySchema>;

const fetcher = async (
  query: string,
  params: { locale: string; defaultLocale: string }
) => {
  const res = await sanityClient.fetch(query, params);
  return res;
};

export const getStaticProps: GetStaticProps<Props> = async ({
  locale = "it",
  defaultLocale = "it",
}) => {
  const queryParams = {
    locale,
    defaultLocale,
  };
  const query = pageQuery;
  const [layout, settings, page] = await Promise.all([
    getMainLayoutQuery(queryParams),
    sanityClient.fetch(settingsQuery, queryParams),
    fetcher(pageQuery, queryParams),
  ]);
  return {
    props: {
      layout,
      settings,
      page,
      query,
      queryParams,
    },
    revalidate: 60,
  };
};

const WishlistPage: NextPage<Props> = (props) => {
  const { page, settings, queryParams } = props;

  useEffect(() => {
    const rawWishlist = localStorage.getItem("wishlist");
    if (rawWishlist) {
      try {
        // Read wishlist from session storage and copy it into the store
        const wishlist = wishlistSchema.parse(JSON.parse(rawWishlist));
        store.dispatch(createWishlist(wishlist));
      } catch (error) {
        console.error(error);
      }
    }

    const unsubscribe = store.dispatch(
      addAppListener({
        matcher: isAnyOf(
          wishlistLineAdd,
          wishlistLineRemove,
          wishlistLineUpdate
        ),
        async effect(_, listenerApi) {
          // Can cancel other running instances
          listenerApi.cancelActiveListeners();
          // Get wishlist state
          const { wishlist } = listenerApi.getState();
          // Sync it with localStorage
          localStorage.setItem("wishlist", JSON.stringify(wishlist));
        },
      })
    );
    return () => {
      unsubscribe();
    };
  }, []);

  // Merge page seo with general seo
  const seo = useMemo(() => {
    if (typeof page.seo !== "object") return {};
    if (typeof settings?.seo !== "object") return {};
    return mergeWith(settings.seo, page.seo, (a, b) => b === null ? a : undefined);
  }, [page?.seo, settings?.seo]);

  return (
    <>
      {seo && <SEO {...seo} slug={page?.slug} />}
      <Wishlist page={page} queryParams={queryParams} />
    </>
  );
};

export default WishlistPage;
